import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    ApiException,
    ScenarioRunClient,
    ScenarioRunSummary,
    ScenarioSuiteSummary,
} from '../../api/api';
import { checkScenarioPreflight } from '../../api/scenarioPreflight';
import {
    deleteScenarioSuite,
    downloadScenarioSuite,
    saveScenarioSuite,
    suiteFailureMessage,
} from '../../api/scenarioSuites';
import { runMomentShort } from '../../utils/scenarioRunFormat';
import { CollapsibleSection } from '../Section/CollapsibleSection';
import { EnvironmentCheck, IEnvironmentCheckState } from './EnvironmentCheck';
import { ScenarioPicker, selectableIds } from './ScenarioPicker';
import { blockedScenarios } from './ScenarioPreflight';
import { ScenarioRunStatus } from './ScenarioRunStatus';

// The API answers a refused start with the reason as the response body, and that reason is written to be read by
// the person who pressed the button.
const startFailureMessage = (error: any) => {
    if (error && ApiException.isApiException(error)) {
        return error.response || error.message;
    }

    return 'Starting the run failed. Check the browser console for details.';
};

export interface ISuiteNotice {
    kind: 'info' | 'success' | 'error';
    text: string;
}

/** A notice as the pages draw it: multi-line, because a refused suite lists every problem on a line of its own. */
export const SuiteNotice = ({
    notice,
    onClose,
}: {
    notice: ISuiteNotice;
    onClose?: () => void;
}) => (
    <div
        className={`arl-suite-notice arl-suite-notice--${notice.kind}`}
        role={notice.kind === 'error' ? 'alert' : 'status'}
    >
        <FontAwesomeIcon
            icon={
                notice.kind === 'error'
                    ? 'circle-exclamation'
                    : notice.kind === 'success'
                    ? 'circle-check'
                    : 'circle-info'
            }
            className="arl-suite-notice__icon"
        />
        <div className="arl-suite-notice__text">{notice.text}</div>
        {onClose && (
            <button
                type="button"
                className="arl-suite-notice__close"
                aria-label="Close"
                onClick={onClose}
            >
                <FontAwesomeIcon icon="xmark" />
            </button>
        )}
    </div>
);

/**
 * What survives the fold of a suite card: how big it is, when it was uploaded and how its last run went. Enough
 * to choose between suites without opening each of them.
 */
const SuiteStatus = ({
    suite,
    lastRun,
}: {
    suite: ScenarioSuiteSummary;
    lastRun?: ScenarioRunSummary;
}) => (
    <>
        {suite.problem ? (
            <span className="scenario-tests__flag scenario-tests__flag--problem">
                <FontAwesomeIcon icon="circle-exclamation" />
                Cannot be read
            </span>
        ) : (
            <span>
                {selectableIds(suite).length} scenario(s) &middot;{' '}
                {(suite.profiles ?? []).length} profile(s)
            </span>
        )}
        {suite.updatedAtUtc && (
            <span>uploaded {runMomentShort(suite.updatedAtUtc)}</span>
        )}
        {lastRun && (
            <span>
                <ScenarioRunStatus
                    status={lastRun.stale ? 'abandoned' : lastRun.status}
                />{' '}
                last run {runMomentShort(lastRun.startedAtUtc)}
            </span>
        )}
    </>
);

export interface ISuiteCardProps {
    customerID: string;
    environment: string;
    suite: ScenarioSuiteSummary;
    lastRun?: ScenarioRunSummary;
    defaultOpen: boolean;
    // Whether a run can be started now: released, and no other run going.
    canStart: boolean;
    // Whether the environment is released for runs from the dashboard, which managing suites is held to too.
    canManage: boolean;
    startBlockedReason?: string;
    // True while any run is starting or going: one run at a time per environment, whichever suite it is.
    busy: boolean;
    onStarted: (runId: string, notice: string) => void;
    onReplaced: (suite: ScenarioSuiteSummary) => void;
    onDeleted: (id: string) => void;
}

/**
 * One suite: what it asks of the environment, what to run of it, and the three things you can do to the suite
 * itself - download it to edit, replace it with an edited version, or delete it.
 *
 * A run always executes exactly one suite, which is why the selection lives inside the card and every card keeps
 * its own.
 */
export const SuiteCard = ({
    customerID,
    environment,
    suite,
    lastRun,
    defaultOpen,
    canStart,
    canManage,
    startBlockedReason,
    busy,
    onStarted,
    onReplaced,
    onDeleted,
}: ISuiteCardProps) => {
    const axios = useAxiosSwagger();
    const id = suite.id ?? suite.name ?? 'suite';

    const [open, setOpen] = useState(defaultOpen);
    const [selected, setSelected] = useState<string[]>([]);
    const [dryRun, setDryRun] = useState(false);
    const [stopOnFailure, setStopOnFailure] = useState(false);
    const [starting, setStarting] = useState(false);
    const [working, setWorking] = useState<
        'replacing' | 'deleting' | 'downloading' | undefined
    >(undefined);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [notice, setNotice] = useState<ISuiteNotice | undefined>(undefined);
    const [environmentCheck, setEnvironmentCheck] =
        useState<IEnvironmentCheckState>({ status: 'checking', checks: [] });
    const checkedFor = useRef<ScenarioSuiteSummary | undefined>(undefined);
    const fileInput = useRef<HTMLInputElement>(null);

    // A suite opens with every scenario ticked, which is what a run without a selection does anyway. Unticking is
    // how somebody narrows it down, and what is ticked is exactly what will run. A replaced suite starts over.
    const allSelectable = useMemo(() => selectableIds(suite), [suite]);

    useEffect(() => setSelected(allSelectable), [allSelectable]);

    // The checks a run makes before its first scenario, for the whole suite so every scenario can be marked. They
    // cost a handful of calls to Exact and Chargebee, so they are made when the card is first opened, again when
    // the suite is replaced, and otherwise only when somebody asks. An environment the dashboard may not run
    // scenarios on is refused them, and then the block says a run checks it instead of showing an error.
    const runEnvironmentCheck = useCallback(async () => {
        if (!axios) {
            return;
        }

        setEnvironmentCheck((current) => ({ ...current, status: 'checking' }));

        try {
            const report = await checkScenarioPreflight(
                axios,
                customerID,
                environment,
                suite.id
            );

            setEnvironmentCheck({
                status: 'done',
                checks: report.checks ?? [],
                checkedAtUtc: report.checkedAtUtc,
            });
        } catch (error: any) {
            console.log(error);

            setEnvironmentCheck(
                error?.response?.status === 400 && !canManage
                    ? { status: 'unavailable', checks: [] }
                    : {
                          status: 'error',
                          checks: [],
                          error: suiteFailureMessage(
                              error,
                              'see the browser console for details'
                          ),
                      }
            );
        }
    }, [axios, customerID, environment, suite.id, canManage]);

    useEffect(() => {
        if (!open || suite.problem || checkedFor.current === suite) {
            return;
        }

        checkedFor.current = suite;
        runEnvironmentCheck();
    }, [open, suite, runEnvironmentCheck]);

    const blocked = useMemo(
        () => blockedScenarios(environmentCheck.checks),
        [environmentCheck.checks]
    );

    const wholeSuite =
        allSelectable.length > 0 &&
        selected.length === allSelectable.length &&
        allSelectable.every((scenario) => selected.includes(scenario));

    const handleStart = async () => {
        if (!axios) {
            return;
        }

        setStarting(true);
        setNotice(undefined);

        try {
            const client = new ScenarioRunClient(undefined, axios);
            const started = await client.startScenarioRun(
                customerID,
                environment,
                // The whole suite goes without --only, exactly as a run from the command line would.
                {
                    suite: suite.id,
                    only: wholeSuite ? [] : selected,
                    skip: [],
                    dryRun,
                    stopOnFailure,
                }
            );

            onStarted(
                started.runId as string,
                `Started run ${started.runId} of ${id}${
                    started.dryRun ? ' as a dry run' : ''
                }: ${
                    wholeSuite
                        ? 'the whole suite'
                        : `${selected.length} scenario(s)`
                }. The runner needs half a minute or so to start up; the run drops into the history below by itself.`
            );
        } catch (error) {
            console.log(error);
            setNotice({ kind: 'error', text: startFailureMessage(error) });
        }

        setStarting(false);
    };

    const handleReplace = async (file?: File) => {
        if (!axios || !file) {
            return;
        }

        setWorking('replacing');
        setNotice(undefined);

        try {
            const saved = await saveScenarioSuite(
                axios,
                customerID,
                environment,
                await file.text(),
                suite.id
            );

            onReplaced(saved);
            setNotice({
                kind: 'success',
                text: `Replaced with ${file.name}: ${
                    selectableIds(saved).length
                } scenario(s) across ${
                    (saved.profiles ?? []).length
                } profile(s). The next run uses this version.`,
            });
        } catch (error) {
            console.log(error);
            setNotice({
                kind: 'error',
                text: suiteFailureMessage(
                    error,
                    'The suite could not be replaced. Check the browser console for details.'
                ),
            });
        }

        setWorking(undefined);
    };

    const handleDownload = async () => {
        if (!axios) {
            return;
        }

        setWorking('downloading');

        try {
            await downloadScenarioSuite(axios, customerID, environment, id);
        } catch (error) {
            console.log(error);
            setNotice({
                kind: 'error',
                text: suiteFailureMessage(
                    error,
                    'The suite could not be downloaded.'
                ),
            });
        }

        setWorking(undefined);
    };

    const handleDelete = async () => {
        if (!axios) {
            return;
        }

        setWorking('deleting');

        try {
            await deleteScenarioSuite(axios, customerID, environment, id);
            onDeleted(id);
        } catch (error) {
            console.log(error);
            setNotice({
                kind: 'error',
                text: suiteFailureMessage(
                    error,
                    'The suite could not be deleted.'
                ),
            });
            setWorking(undefined);
            setConfirmDelete(false);
        }
    };

    // A run reads its suite once when it starts, so a suite can be changed while one is going.
    const manageDisabled = !canManage || !!working;
    const manageTitle = !canManage ? startBlockedReason ?? '' : '';

    const tools = confirmDelete ? (
        <span className="arl-suite-tools arl-suite-tools--confirm">
            <span>
                Delete <strong>{id}</strong>? Its runs stay in the history.
            </span>
            <button
                type="button"
                className="arl-suite-tools__btn arl-suite-tools__btn--danger"
                disabled={!!working}
                onClick={handleDelete}
            >
                {working === 'deleting' ? 'Deleting...' : 'Yes, delete'}
            </button>
            <button
                type="button"
                className="arl-suite-tools__btn"
                disabled={!!working}
                onClick={() => setConfirmDelete(false)}
            >
                Cancel
            </button>
        </span>
    ) : (
        <span className="arl-suite-tools">
            <button
                type="button"
                className="arl-suite-tools__btn"
                disabled={!!working}
                onClick={handleDownload}
                title="The suite as it was uploaded, comments included, to edit and upload again"
            >
                <FontAwesomeIcon icon="cloud-arrow-down" />
                {working === 'downloading' ? 'Downloading...' : 'Download'}
            </button>
            <button
                type="button"
                className="arl-suite-tools__btn"
                disabled={manageDisabled}
                title={
                    manageTitle ||
                    'Upload an edited version. The file has to carry the same name.'
                }
                onClick={() => fileInput.current?.click()}
            >
                <FontAwesomeIcon icon="rotate" />
                {working === 'replacing' ? 'Replacing...' : 'Replace'}
            </button>
            <button
                type="button"
                className="arl-suite-tools__btn arl-suite-tools__btn--quiet-danger"
                disabled={manageDisabled}
                title={manageTitle}
                onClick={() => setConfirmDelete(true)}
            >
                <FontAwesomeIcon icon="trash-can" />
                Delete
            </button>
            <input
                ref={fileInput}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={(event) => {
                    handleReplace(event.target.files?.[0]);
                    // Cleared, so choosing the same file again after fixing it still fires a change.
                    event.target.value = '';
                }}
            />
        </span>
    );

    const startButton = (
        <button
            type="button"
            className="btn arl-btn__login"
            disabled={busy || starting || !canStart || selected.length === 0}
            title={startBlockedReason ?? ''}
            onClick={handleStart}
        >
            {starting
                ? 'Starting...'
                : busy
                ? 'A run is going...'
                : `Start ${dryRun ? 'a dry run of ' : ''}${
                      wholeSuite
                          ? 'the whole suite'
                          : `${selected.length} scenario(s)`
                  }`}
        </button>
    );

    return (
        <CollapsibleSection
            id={`suite-${id}`}
            title={suite.name ?? id}
            status={<SuiteStatus suite={suite} lastRun={lastRun} />}
            tools={tools}
            open={open}
            onToggle={() => setOpen(!open)}
            className="scenario-tests__suite"
        >
            {notice && (
                <SuiteNotice
                    notice={notice}
                    onClose={() => setNotice(undefined)}
                />
            )}

            {suite.problem ? (
                <div className="arl-suite-problem">
                    <p className="arl-suite-problem__lead">
                        A run would refuse this suite as well. Download it, fix
                        what is listed and replace it; or delete it.
                    </p>
                    <pre className="arl-suite-problem__text">
                        {suite.problem}
                    </pre>
                    <code className="arl-picker__origin">{suite.origin}</code>
                </div>
            ) : (
                <ScenarioPicker
                    suite={suite}
                    selected={selected}
                    dryRun={dryRun}
                    stopOnFailure={stopOnFailure}
                    disabled={busy || starting || !canStart}
                    onSelectedChange={setSelected}
                    onDryRunChange={setDryRun}
                    onStopOnFailureChange={setStopOnFailure}
                    action={startButton}
                    blocked={blocked}
                    environment={
                        <EnvironmentCheck
                            steps={suite.manualSteps ?? []}
                            check={environmentCheck}
                            onRecheck={runEnvironmentCheck}
                        />
                    }
                />
            )}
        </CollapsibleSection>
    );
};
